import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";
import { Loader2, Star as StarIcon, MapPin, Sparkles } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import NumericRating from "@/components/NumericRating";
import { useAuth } from "@/lib/auth";

export default function RatedByYou() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [minRating, setMinRating] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = async (mr) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rated-by-you?min_rating=${mr}`);
      setItems(data.rated || []);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(minRating); }, [minRating]);

  const ratePhotoInline = async (photoId, ownerId, rating) => {
    try {
      const { data: r } = await api.post("/rate", { photo_id: photoId, rating });
      trackEvent("rate_photo", { score: rating, is_high: rating >= 8, source: "rated_by_you" });
      // If the new rating drops below the current filter, remove it from view;
      // otherwise just update the stored my_rating in place.
      if (rating < minRating) {
        setItems((prev) => prev.filter((it) => it.photo_id !== photoId));
        toast(`Removed — you rated it ${rating}/10, below the ${minRating}+ filter`);
      } else {
        setItems((prev) => prev.map((it) => it.photo_id === photoId ? { ...it, my_rating: rating } : it));
        if (r.streak?.credit_awarded) toast.success(`\uD83D\uDD25 ${r.streak.streak}-day streak! +1 credit`, { duration: 4200 });
        else toast.success(`Updated to ${rating}/10`);
      }
      if (refresh) refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not save rating"));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-14">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#c39d2b]" />
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Rated by you</span>
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mt-2 leading-tight">Photos you loved.</h1>
          <p className="mt-3 text-lg text-[#5c5c5c]">Photos you rated {minRating}/10 or higher, newest first.</p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="rby-filter">
          {[6, 7, 8, 9, 10].map((n) => (
            <button
              key={n}
              onClick={() => setMinRating(n)}
              className={`w-10 h-10 rounded-full text-sm font-bold ${
                n === minRating
                  ? "bg-[#722f37] text-white shadow"
                  : "bg-white text-[#722f37] border border-[#d6cfc0] hover:border-[#722f37]"
              }`}
              data-testid={`rby-filter-${n}`}
            >
              {n}+
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-[#e5e0d8] card-elev text-center" data-testid="rby-empty">
          <StarIcon className="w-8 h-8 text-[#c39d2b] mx-auto" />
          <h3 className="font-serif-display text-3xl mt-4 text-[#722f37]">No matches for this filter.</h3>
          <p className="mt-3 text-[#5c5c5c] text-lg">Head to Discover and rate a few photos to build your favourites.</p>
          <div className="mt-6"><Link to="/discover" className="btn-primary" data-testid="rby-go-discover">Rate photos</Link></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rby-grid">
          {items.map((r) => (
            <RatedByYouCard
              key={r.photo_id}
              item={r}
              onRate={(score) => ratePhotoInline(r.photo_id, r.owner.id, score)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RatedByYouCard({ item: r, onRate }) {
  const [busy, setBusy] = useState(false);
  const handle = async (score) => {
    setBusy(true);
    try { await onRate(score); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d8] card-elev flex flex-col" data-testid={`rby-item-${r.photo_id}`}>
      <Link to={`/u/${r.owner.id}`} className="relative aspect-[4/5] bg-[#eae5d9] block" data-testid={`rby-view-${r.photo_id}`}>
        <img src={r.data_url} alt={r.owner.display_name} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3 rounded-full px-3 py-1.5 text-xs font-bold bg-[#722f37] text-white flex items-center gap-1">
          <StarIcon className="w-3.5 h-3.5 text-[#e5c65b] fill-[#e5c65b]" /> You: {r.my_rating}/10
        </div>
        {r.owner.verified && (
          <div className="absolute top-3 right-3"><VerifiedBadge size={12} /></div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="font-serif-display text-white text-xl leading-tight">
            {r.owner.display_name}, {r.owner.age}
          </div>
          {r.owner.location && (
            <div className="flex items-center gap-1 text-white/85 text-xs mt-0.5">
              <MapPin className="w-3 h-3" /> {r.owner.location}
            </div>
          )}
        </div>
      </Link>
      <div className="p-3 bg-[#faf9f6] border-t border-[#e5e0d8]" data-testid={`rby-rate-strip-${r.photo_id}`}>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#722f37] mb-1.5">
          Re-rate this photo
        </div>
        <NumericRating
          value={r.my_rating || 0}
          onChange={handle}
          readOnly={busy}
          testId={`rby-rate-${r.photo_id}`}
          compact
        />
      </div>
    </div>
  );
}
